import { useEffect, useState } from "react";

import {
  documentUrlDataType,
  doNotImport,
  emailDataType,
  phoneDataType,
  textDataType,
  validateSpreadSheetCell,
} from "../lib/spreadsheet-upload-config";
import {
  isValidDocumentURL,
  isValidEmail,
  isValidPhoneNumber,
} from "../lib/validators";
import { Button } from "./ui/button";
import EditableTD from "./editable-td";
import { SpreadSheetState } from "./spread-sheet-upload-flow";
import { Info } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";
import { Switch } from "./ui/switch";
import { cn } from "../lib/utils";

// Define interfaces for the data structures
interface SupportedColumn {
  value: string;
  label: string;
  type: string;
  isRequired: boolean;
}

interface MappedColumn {
  originalColumn: string;
  supportedColumn: string;
  supportedColumnDataType: string;
  supportedColumnLabel: string;
  isRequired: boolean;
}

interface RowData {
  id: string;
  [key: string]: string | undefined;
}

interface TableHeader {
  supportedColumn: string;
  supportedColumnLabel: string;
  supportedColumnDataType: string;
  isRequired: boolean;
  originalColumn: string;
}

interface SpreadSheetDataValidationProps {
  uploadedSpreadSheet: SpreadSheetState | null;
  setUploadedSpreadSheet: (state: SpreadSheetState) => void;
}

const SpreadSheetDataValidation: React.FC<SpreadSheetDataValidationProps> = ({
  uploadedSpreadSheet,
  setUploadedSpreadSheet,
}) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showRowsWithErrors, setShowsRowsWithErrrors] = useState<boolean>(true);
  const [showUnmatchedColumns, setShowsUnmatchedColumns] =
    useState<boolean>(true);

  const unmatchedColumns: TableHeader[] = (
    uploadedSpreadSheet?.supportedColumns || []
  )
    .filter((column) => column.value !== doNotImport)
    .map((column) => ({
      supportedColumn: column.value,
      supportedColumnLabel: column.label,
      supportedColumnDataType: column.type ?? "",
      isRequired: column.isRequired ?? false,
      originalColumn:
        uploadedSpreadSheet?.mappedColumns.find(
          (mappedColumn) => mappedColumn.supportedColumn === column.value
        )?.originalColumn || "",
    }));

  const [tableHeaders, setTableHeaders] =
    useState<TableHeader[]>(unmatchedColumns);
  const [countErrors, setCountErrors] = useState<number>(0);

  const handleSelectedRow = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (event.target.checked) {
      setSelectedRows((prev) => [...prev, id]);
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const handleDeleteSelectedRows = () => {
    const newRowsData = uploadedSpreadSheet?.rowsData.filter(
      (item) => !selectedRows.includes(item.id.toString())
    );
    const newInitialState: SpreadSheetState = {
      ...uploadedSpreadSheet!,
      rowsData: newRowsData || [],
    };
    setSelectedRows([]);
    setUploadedSpreadSheet(newInitialState);
  };

  useEffect(() => {
    let countErrors = 0;
    let alreadyCountError = false;
    const arrayLength = (uploadedSpreadSheet?.rowsData.length || 1) - 1;

    uploadedSpreadSheet?.rowsData.forEach((item, index) => {
      alreadyCountError = false;
      unmatchedColumns.forEach((column) => {
        const value = item[column.originalColumn]?.toString()?.trim();
        if (column.supportedColumnDataType === textDataType) {
          if (value?.length > 100 || column.originalColumn === "") {
            if (!alreadyCountError) {
              countErrors += 1;
              alreadyCountError = true;
            }
          }
        } else if (column.supportedColumnDataType === emailDataType) {
          if (!isValidEmail(value)) {
            if (!alreadyCountError) {
              countErrors += 1;
              alreadyCountError = true;
            }
          }
        } else if (column.supportedColumnDataType === phoneDataType) {
          if (!isValidPhoneNumber(value)) {
            if (!alreadyCountError) {
              countErrors += 1;
              alreadyCountError = true;
            }
          }
        } else if (column.supportedColumn === documentUrlDataType) {
          if (!isValidDocumentURL(value)) {
            if (!alreadyCountError) {
              countErrors += 1;
              alreadyCountError = true;
            }
          }
        }
      });
      if (arrayLength === index) {
        setCountErrors(countErrors);
      }
    });
  }, [unmatchedColumns, uploadedSpreadSheet]);

  return (
    <div className="flex flex-grow flex-col space-y-8 px-6">
      <div className="flex flex-col space-y-4 text-center">
        <h5>Review your import</h5>
        <p>Correct the issues and fill the missing data.</p>
        <div className="flex justify-center space-x-6">
          <p className="flex">
            {(uploadedSpreadSheet?.rowsData.length || 0) - countErrors} items
            updated or added
            <Info className="text-green-600 w-4 mx-2" />
          </p>
          <p className="flex">
            {countErrors} items not updated or added due to errors
            <Info className="text-red-600 w-4 mx-2" />
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch
            checked={showRowsWithErrors}
            onCheckedChange={(checked) => setShowsRowsWithErrrors(checked)}
            className={cn(
              "h-6 w-11",
              "bg-gray-200 data-[state=checked]:bg-primary-600",
              "border-2 border-transparent"
            )}
          />
          <p>Show only rows with errors</p>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={showUnmatchedColumns}
            onCheckedChange={(checked) => {
              if (!showUnmatchedColumns) {
                setTableHeaders(unmatchedColumns);
              } else {
                setTableHeaders(
                  (uploadedSpreadSheet?.mappedColumns || [])
                    .filter((column) => column.supportedColumn !== doNotImport)
                    .filter((column) => column.supportedColumn !== "")
                );
              }
              setShowsUnmatchedColumns(checked);
            }}
            className={cn(
              "h-6 w-11",
              "bg-gray-200 data-[state=checked]:bg-primary-600",
              "border-2 border-transparent"
            )}
          />
          <p>Show unmatched columns</p>
        </div>
        <Button
          variant="destructive"
          disabled={selectedRows.length === 0}
          onClick={handleDeleteSelectedRows}
        >
          Remove
        </Button>
      </div>
      <div className="h-64 overflow-y-auto rounded-xl border border-gray-200">
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableColumnHeader> </TableColumnHeader>
              <TableColumnHeader>Id</TableColumnHeader>
              {tableHeaders.map((column, index) => (
                <TableColumnHeader key={index}>
                  {column.supportedColumnLabel}
                </TableColumnHeader>
              ))}
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {uploadedSpreadSheet?.rowsData.length || 0 ? (
              uploadedSpreadSheet!.rowsData
                .filter((item) => {
                  if (!showRowsWithErrors) {
                    return true;
                  }
                  return unmatchedColumns.some((column) => {
                    const value = item[column.originalColumn]
                      ?.toString()
                      ?.trim();
                    return !validateSpreadSheetCell(
                      column.supportedColumnDataType,
                      value,
                      column.isRequired
                    );
                  });
                })
                .map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="pl-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.id.toString())}
                        onChange={(event) =>
                          handleSelectedRow(event, item.id.toString())
                        }
                      />
                    </TableCell>
                    <TableCell>{item.id}</TableCell>
                    {tableHeaders.map((column, tableHeaderIndex) => {
                      const value = item[column.originalColumn]
                        ?.toString()
                        ?.trim();
                      const invalidData = !validateSpreadSheetCell(
                        column.supportedColumnDataType,
                        value,
                        column.isRequired
                      );
                      const errorMessage =
                        column.isRequired &&
                        (value === "" || value === undefined)
                          ? `${column.supportedColumnLabel} is required`
                          : `${column.supportedColumnLabel} is invalid`;

                      return (
                        <EditableTD
                          key={tableHeaderIndex}
                          value={value || ""}
                          onChange={(cellValue: string) => {
                            if (column.originalColumn !== "") {
                              const newRowsData = [
                                ...uploadedSpreadSheet!.rowsData,
                              ];
                              newRowsData[index][column.originalColumn] =
                                cellValue;
                              const newFileUploadState: SpreadSheetState = {
                                ...uploadedSpreadSheet!,
                                rowsData: newRowsData,
                              };
                              setUploadedSpreadSheet(newFileUploadState);
                            }
                          }}
                        />
                      );
                    })}
                  </TableRow>
                ))
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-sm font-medium text-gray-500"
                >
                  No items Found.
                </td>
              </tr>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SpreadSheetDataValidation;
